import { useI18n, type CommonKey } from '@hive/i18n';
import { useEffect, useId, type ReactNode } from 'react';
import { PageHelpButton } from './help';
import { Icon, type IconName } from './icons';

export function cx(...c: Array<string | false | null | undefined>): string {
  return c.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------- marca

export function Brand({ productName = 'Hive', logoUrl, compact }: { productName?: string; logoUrl?: string | null; compact?: boolean }) {
  return (
    <span className="brand">
      {logoUrl ? (
        <img src={logoUrl} alt="" style={{ height: 26, maxWidth: 120, objectFit: 'contain' }} referrerPolicy="no-referrer" />
      ) : (
        <span className="brand-mark" aria-hidden="true" />
      )}
      {!compact && <span className="brand-word">{productName}</span>}
    </span>
  );
}

// ---------------------------------------------------------------- casca da aplicação

/**
 * Casca: cabeçalho + menu lateral + área de trabalho (+ rodapé de status).
 * No layout "rail" (A + C): trilho escuro e painel de contexto na coluna da esquerda, de cima a baixo; à direita, o
 * cabeçalho de 60 px, a tela e o rodapé de status (`statusBar`, 34 px, preso embaixo).
 * Abaixo de 960 px o menu vira gaveta (abre pelo botão da barra e fecha com Esc ou no fundo).
 */
export function AppShell({
  topbar,
  nav,
  navLabel,
  navOpen,
  onCloseNav,
  footer,
  children,
  layout,
  bottomNav,
  subCollapsed,
  statusBar,
}: {
  topbar: ReactNode;
  nav: ReactNode;
  navLabel: string;
  navOpen: boolean;
  onCloseNav: () => void;
  footer?: ReactNode;
  children: ReactNode;
  /** "rail": trilho de espaços com o submenu ao lado (layout modelo 1). */
  layout?: 'rail';
  /** Barra de abas embaixo, no celular (no layout de trilho). */
  bottomNav?: ReactNode;
  /** No trilho: o submenu (segunda coluna) recolhido; a tela ganha a largura dele. */
  subCollapsed?: boolean;
  /** Rodapé de status (normalmente `<StatusBar>`), preso ao pé da tela. */
  statusBar?: ReactNode;
}) {
  const { t } = useI18n<CommonKey>();
  useEffect(() => {
    document.body.classList.toggle('nav-open', navOpen);
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseNav();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen, onCloseNav]);
  return (
    <>
      <a className="skip" href="#main">
        {t('ui.skip')}
      </a>
      <div className={cx('app', layout === 'rail' && 'rail-layout', layout === 'rail' && subCollapsed && 'sub-collapsed')}>
        <header className="topbar">{topbar}</header>
        <aside className="sidebar" id="sidebar" aria-label={navLabel}>
          {nav}
        </aside>
        <div className="scrim" onClick={onCloseNav} aria-hidden="true" />
        <main id="main" tabIndex={-1}>
          <div className="view">{children}</div>
          {footer && <footer className="foot">{footer}</footer>}
        </main>
        {statusBar && <footer className="app-status">{statusBar}</footer>}
        {bottomNav}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- cabeçalho de página

export function PageHead({
  title,
  eyebrow,
  sub,
  actions,
  help = true,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  /** Botão "Ajuda" da tela (abre o painel no artigo dela); false onde não faz sentido (a própria central de ajuda). */
  help?: boolean;
}) {
  return (
    <div className="page-head">
      <div className="ph-text">
        {eyebrow && <div className="ph-eyebrow">{eyebrow}</div>}
        <h1 tabIndex={-1} data-page-title>
          {title}
        </h1>
        {sub && <p className="ph-sub">{sub}</p>}
      </div>
      {(actions || help) && (
        <div className="ph-actions">
          {actions}
          {help && <PageHelpButton />}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- painéis e cartões

export function Panel({
  title,
  actions,
  children,
  foot,
  pad,
  className,
  headingLevel = 2,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  foot?: ReactNode;
  pad?: boolean;
  className?: string;
  headingLevel?: 2 | 3;
}) {
  const id = useId();
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={cx('panel', pad && !title && 'panel-pad', className)} aria-labelledby={title ? id : undefined}>
      {title && (
        <div className="panel-head">
          <H id={id}>{title}</H>
          {actions && (
            <>
              <span className="spacer" />
              {actions}
            </>
          )}
        </div>
      )}
      {title ? <div className="panel-body">{children}</div> : children}
      {foot && <div className="panel-foot">{foot}</div>}
    </section>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <article className={cx('card', className)}>{children}</article>;
}

// ---------------------------------------------------------------- avisos e estados

export type Tone = 'info' | 'ok' | 'warn' | 'crit';

export function Note({ tone = 'info', children, icon }: { tone?: Tone; children: ReactNode; icon?: IconName }) {
  const ic: IconName = icon ?? (tone === 'ok' ? 'check' : tone === 'info' ? 'info' : 'alert');
  return (
    <div className={cx('note', tone !== 'info' && tone)}>
      <Icon name={ic} />
      <div className="grow">{children}</div>
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children && <span>{children}</span>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row" role="status">
      <span className="dot-run" aria-hidden="true" />
      {label && <span className="muted small">{label}</span>}
    </span>
  );
}

export function Loading() {
  const { t } = useI18n<CommonKey>();
  return (
    <div style={{ padding: '24px 0' }}>
      <Spinner label={t('ui.loading')} />
    </div>
  );
}

// ---------------------------------------------------------------- marcadores

export type PillTone = 'ok' | 'warn' | 'crit' | 'info' | 'accent' | 'neutral';

export function Pill({ tone = 'neutral', children, title }: { tone?: PillTone; children: ReactNode; title?: string }) {
  return (
    <span className={cx('pill', tone !== 'neutral' && `pill-${tone}`)} title={title}>
      {children}
    </span>
  );
}

export function Chip({ children, on, onClick, onRemove, removeLabel }: {
  children: ReactNode;
  on?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  removeLabel?: string;
}) {
  if (onClick) {
    return (
      <button type="button" className={cx('chip', on && 'on')} aria-pressed={on} onClick={onClick}>
        {children}
      </button>
    );
  }
  return (
    <span className={cx('chip', on && 'on')}>
      {children}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={removeLabel}>
          <Icon name="x" size="sm" />
        </button>
      )}
    </span>
  );
}

export type StatusKind = 'ok' | 'warn' | 'crit' | 'idle' | 'run';
export function Status({ kind, children }: { kind: StatusKind; children: ReactNode }) {
  return <span className={`st st-${kind}`}>{children}</span>;
}

export function Meter({ value, max, tone, label }: { value: number; max: number; tone?: 'ok' | 'warn' | 'crit'; label?: string }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className={cx('meter', tone)} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      <span style={{ width: pct.toFixed(1) + '%' }} />
    </div>
  );
}

// ---------------------------------------------------------------- listas de valores

export function KeyValue({ rows }: { rows: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="kv">
      {rows.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Kpi({
  label,
  value,
  foot,
  icon,
  tone,
  children,
}: {
  label: ReactNode;
  value: ReactNode;
  foot?: ReactNode;
  icon?: IconName;
  /** cor do número quando ele pede atenção */
  tone?: 'crit' | 'warn' | 'ok';
  children?: ReactNode;
}) {
  return (
    <div className={`panel kpi${tone ? ` kpi-${tone}` : ''}`}>
      <span className="kpi-label">
        {label}
        {icon && <Icon name={icon} size="sm" />}
      </span>
      <span className="kpi-value">{value}</span>
      {children}
      {foot && <span className="kpi-foot">{foot}</span>}
    </div>
  );
}

// ---------------------------------------------------------------- barras horizontais

export interface HBarItem {
  label: ReactNode;
  value: number;
  color?: string;
  text?: ReactNode;
  title?: string;
}

/** Barras horizontais do protótipo (versões, último contato…): rótulo, trilho e valor. */
export function HBars({ items, max, label }: { items: HBarItem[]; max?: number; label?: string }) {
  const top = max ?? Math.max(1, ...items.map((x) => x.value));
  return (
    <div className="hbars" role="list" aria-label={label}>
      {items.map((x, i) => (
        <div className="hbar" role="listitem" key={i}>
          <span className="l" title={x.title}>
            {x.label}
          </span>
          <span className="track" aria-hidden="true">
            <span className="fill" style={{ width: `${Math.max(0.5, (x.value / top) * 100).toFixed(1)}%`, background: x.color ?? 'var(--s1)' }} />
          </span>
          <span className="v">{x.text ?? x.value}</span>
        </div>
      ))}
    </div>
  );
}
